
/// <amd-module name="Work/Views/MyWork/DataProviders/MyWorkProviderBase" />
import { IVssPageContext, Services } from "VSS/Platform/Context";
import { Observable } from "VSS/Core/Observable";
import { IMyWorkProvider, ProviderState, IMyWorkProviderStateChangeArgs } from "DataProviders/IMyWorkProvider";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";


export const activityDateFieldName: string = "System.MyWork.FormattedDate";
/**
 * Base data provider class, implements IMyWorkProvider in an abstract way.
 */
export abstract class MyWorkProviderBase<T> extends Observable<IMyWorkProviderStateChangeArgs> implements IMyWorkProvider {

    protected context: IVssPageContext;
    protected queryResult: T;
    private queryPromise: Promise<T> | null;
    private providerState: ProviderState = ProviderState.Uninitialized;

    constructor(context: IVssPageContext) {
        super();
        this.context = context;
    }

    public initialize(): void {
        if (this.providerState === ProviderState.Uninitialized) {
            this.getDataInternal();
        }
    }

    public getWorkItemsCount(): number {
        return this.getDataLength();
    }

    public getLimitExceeded(): boolean {
        return false;
    }

    /**
     * Gets the current state of the provider.
     */
    public getState(): ProviderState {
        return this.providerState;
    }

    public abstract getWorkItems(): WorkItem[];

    /**
     * Makes the rest call to retrieve the data.
     */
    protected abstract getData(): Promise<T>;

    /**
     * Gets the number of items in the list.  Will only be called once getData is complete.
     */
    protected abstract getDataLength(): number;

    /**
     * Sets the current state of the provider.  Fires events when the state changes.
     */
    protected setState(state: ProviderState): void {
        if (state !== this.providerState) {
            const previousState = this.providerState;
            this.providerState = state;
            this.notify({ provider: this, state: state, previousState: previousState }, "changed");
        }
    }

    /**
     * Gets or creates the promise to get the data from the server.  Handles setting the provider state.
     */
    private getDataInternal(): Promise<T> {
        if (this.queryPromise) {
            return this.queryPromise;
        }

        this.setState(ProviderState.Loading);

        this.queryPromise = this.getData();

        return this.queryPromise.then((data: T) => {

            // clear the promise on success only, on failure continue to return the rejected promise.
            this.queryPromise = null;
            this.queryResult = data;

            this.setState(this.getDataLength() > 0 ? ProviderState.Loaded : ProviderState.LoadedZeroData);

            return data;
        }, (reason) => {
            // todo: telemetry
            this.setState(ProviderState.Error);
            return Promise.reject(reason);
        });
    }

}
